import { useQuery } from '@tanstack/react-query';
import type { BranchComparisonReport, BranchComparisonRow, ReportExportRequest } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { KpiCard } from '@/components/KpiCard';
import { ReportPeriodButton, ReportToolbar } from '@/components/ReportToolbar';
import { DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { ExportMenu } from '@/components/ExportMenu';
import { downloadReportExport } from '@/features/reports/api';
import type { ExportFormat } from '@/features/reports/api';
import { branchComparisonKeys, fetchBranchComparison } from '@/features/reports/branch-comparison-api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { usePortal } from '@/features/portal/PortalContext';
import { exportSubtitle } from '@/features/reports/export-table';

const money = (value: number) => value.toFixed(2);
const percent = (value: number | null) => (value === null ? '—' : `${value.toFixed(1)}%`);
const signed = (value: number) => (
  <span className={value < -0.004 ? 'text-red-700' : undefined}>{money(value)}</span>
);

const COLUMNS: Column<BranchComparisonRow>[] = [
  { key: 'branchName', header: 'Branch', pinned: true, cell: (row) => row.branchName },
  { key: 'bills', header: 'Bills', align: 'right', cell: (row) => String(row.bills) },
  { key: 'grossSales', header: 'Gross sales', align: 'right', cell: (row) => money(row.grossSales) },
  { key: 'returns', header: 'Returns', align: 'right', cell: (row) => money(row.returns) },
  { key: 'netSales', header: 'Net sales', align: 'right', cell: (row) => money(row.netSales) },
  { key: 'vat', header: 'VAT', align: 'right', cell: (row) => money(row.vat) },
  { key: 'averageBill', header: 'Avg bill', align: 'right', cell: (row) => money(row.averageBill) },
  { key: 'grossProfit', header: 'Gross profit', align: 'right', cell: (row) => signed(row.grossProfit) },
  { key: 'expenses', header: 'Expenses', align: 'right', cell: (row) => money(row.expenses) },
  { key: 'net', header: 'Net', align: 'right', cell: (row) => signed(row.net) },
  { key: 'share', header: 'Share', align: 'right', cell: (row) => percent(row.share) },
];

/** Cost-derived columns — hidden, on screen and in the export, from a login without cost access. */
const COST_KEYS = new Set(['grossProfit', 'net']);

function visibleColumns(canViewCost: boolean): Column<BranchComparisonRow>[] {
  return canViewCost ? COLUMNS : COLUMNS.filter((column) => !COST_KEYS.has(column.key));
}

const NOTE_SALES =
  'Gross, returns, net sales and VAT are bill amounts including VAT; returns count on their own date, to the branch that took them.';
const NOTE_PROFIT =
  'Gross profit is excl. VAT and after bill discounts: sales less returns less cost of goods sold, costed as on the Profit & loss report. Net is gross profit less expenses.';
const NOTE_EXCLUDED = 'Voided sales and opening balances are left out.';

function linesWithoutCostNote(count: number): string {
  return `${count} sold line${count === 1 ? ' has' : 's have'} no cost recorded, so gross profit is overstated by whatever those items cost.`;
}

/** The table as shown, for the server's PDF / Excel renderer. */
function buildExport(report: BranchComparisonReport, canViewCost: boolean): ReportExportRequest {
  const columns = visibleColumns(canViewCost);
  const totals = totalsCells(report.totals);
  const cell = (row: BranchComparisonRow, key: string): string => {
    switch (key) {
      case 'branchName':
        return row.branchName;
      case 'bills':
        return String(row.bills);
      case 'share':
        return percent(row.share);
      default:
        return money(row[key as 'grossSales']);
    }
  };
  return {
    title: 'Branch comparison',
    subtitle: exportSubtitle(report),
    from: report.from,
    to: report.to,
    columns: columns.map((column) => ({
      header: column.header,
      align: column.align === 'right' ? 'right' : 'left',
      money: column.key !== 'branchName' && column.key !== 'bills' && column.key !== 'share',
      numeric: column.key === 'bills',
    })),
    rows: report.rows.map((row) => columns.map((column) => cell(row, column.key))),
    // The renderer puts the label in the first column; cells cover the rest.
    totals: { label: 'Total', cells: columns.slice(1).map((column) => totals[column.key] ?? '') },
    notes: [
      canViewCost ? `${NOTE_SALES} ${NOTE_PROFIT} ${NOTE_EXCLUDED}` : `${NOTE_SALES} ${NOTE_EXCLUDED}`,
      ...(canViewCost && report.totals.linesWithoutCost > 0 ? [linesWithoutCostNote(report.totals.linesWithoutCost)] : []),
    ],
  };
}

function totalsCells(t: BranchComparisonReport['totals']): Record<string, string> {
  return {
    bills: String(t.bills),
    grossSales: money(t.grossSales),
    returns: money(t.returns),
    netSales: money(t.netSales),
    vat: money(t.vat),
    averageBill: money(t.averageBill),
    grossProfit: money(t.grossProfit),
    expenses: money(t.expenses),
    net: money(t.net),
    share: percent(t.share),
  };
}

/** Net sales per branch as plain horizontal bars, scaled to the largest. */
function NetSalesBars({ rows }: { rows: BranchComparisonRow[] }) {
  const max = Math.max(0, ...rows.map((r) => r.netSales));
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4" aria-labelledby="branch-bars-heading">
      <h2 id="branch-bars-heading" className="mb-3 text-sm font-semibold text-slate-900">
        Net sales by branch
      </h2>
      <ul className="space-y-2">
        {rows.map((row) => {
          const width = max > 0 ? Math.max(0, (row.netSales / max) * 100) : 0;
          return (
            <li key={row.branchId ?? row.branchName} className="grid grid-cols-[minmax(6rem,12rem)_1fr_auto] items-center gap-3 text-sm">
              <span className="truncate text-slate-700" title={row.branchName}>
                {row.branchName}
              </span>
              <span className="h-4 rounded bg-slate-100" aria-hidden="true">
                <span className="block h-4 rounded bg-emerald-600" style={{ width: `${width}%` }} />
              </span>
              <span className="w-32 text-right tabular-nums text-slate-900">
                {money(row.netSales)}
                <span className="ml-2 text-xs text-slate-500">{percent(row.share)}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function BranchComparisonPage() {
  const { profile } = usePortal();
  const canViewCost = profile?.canViewCost !== false;
  const [filters, setFilters] = useReportFilters();
  const toolbar = useReportToolbar(filters, setFilters);

  const query = useQuery({
    queryKey: branchComparisonKeys.report(filters.from, filters.to),
    queryFn: ({ signal }) => fetchBranchComparison(filters, signal),
  });

  return (
    <div>
      <PageHeader
        title="Branch comparison"
        description={`Every branch side by side for the selected window — sales, returns, ${canViewCost ? 'profit and ' : ''}expenses.`}
      />
      <ReportToolbar>
        <ReportPeriodButton from={filters.from} to={filters.to} onChange={toolbar.setRange} />
        {query.data && query.data.rows.length > 0 ? (
          <div className="ml-auto">
            <ExportMenu
              onExport={(format: ExportFormat) => downloadReportExport(buildExport(query.data, canViewCost), format)}
            />
          </div>
        ) : null}
      </ReportToolbar>
      <AsyncSection
        state={query}
        isEmpty={(report: BranchComparisonReport) => report.rows.length === 0}
        emptyTitle="No branches yet"
        skeleton={<TableSkeleton rows={4} columns={11} />}
      >
        {(report: BranchComparisonReport) => (
          <div className="space-y-6">
            <div className={`grid grid-cols-2 gap-4 ${canViewCost ? 'lg:grid-cols-4' : ''}`}>
              <KpiCard label="Net sales" value={money(report.totals.netSales)} />
              {canViewCost ? <KpiCard label="Gross profit" value={money(report.totals.grossProfit)} /> : null}
              <KpiCard label="Expenses" value={money(report.totals.expenses)} />
              {canViewCost ? <KpiCard label="Net" value={money(report.totals.net)} /> : null}
            </div>
            <DataTable
              caption="Branch comparison"
              columns={visibleColumns(canViewCost)}
              rows={report.rows}
              rowKey={(row) => row.branchId ?? row.branchName}
              totals={{ label: 'Total', cells: totalsCells(report.totals) }}
            />
            {report.rows.length > 1 ? <NetSalesBars rows={report.rows} /> : null}
            <p className="text-xs text-slate-500">
              {NOTE_SALES} {canViewCost ? `${NOTE_PROFIT} ` : ''}
              {NOTE_EXCLUDED}
            </p>
            {canViewCost && report.totals.linesWithoutCost > 0 ? (
              <p className="rounded-md bg-orange-50 px-3 py-2 text-sm text-orange-800">
                {linesWithoutCostNote(report.totals.linesWithoutCost)}
              </p>
            ) : null}
          </div>
        )}
      </AsyncSection>
    </div>
  );
}
